<template>
    <div>
        <btns-nav
                v-if="isMobile"
                :items="btns"
                class="mb-4"
        ></btns-nav>
        <card
            :label-top-left="$t('msg.finance')"
            :label-bottom-left="$t('txt.finance.desc')"
            icon="mdi-currency-usd"
        >
            <template
                slot="header"
            >
                <v-btn
                    small
                    color="primary"
                    class="mr-3"
                    :to="{
                        name: 'create_transaction',
                        query: getQueryCreateTransaction
                    }"
                    :fab="$vuetify.breakpoint.smAndDown"
                    elevation="0"
                >
                    <v-icon
                        :class="{'mr-1': !$vuetify.breakpoint.smAndDown}"
                    >mdi-plus</v-icon>
                    <template v-if="!$vuetify.breakpoint.smAndDown">{{ $t('msg.addTransaction') }}</template>
                </v-btn>
                <v-btn
                    small
                    color="primary"
                    :to="{
                        name: 'transfer',
                        query: getQueryCreateTransaction
                    }"
                    :fab="$vuetify.breakpoint.smAndDown"
                    elevation="0"
                >
                    <v-icon
                        size="20"
                        :class="{'mr-1': !$vuetify.breakpoint.smAndDown}"
                    >mdi-repeat</v-icon>
                    <template v-if="!$vuetify.breakpoint.smAndDown">{{ $t('msg.addTransfer') }}</template>
                </v-btn>
            </template>
            <template v-slot:bottom>
                <filters
                    v-model="filters"
                    name="transactions"
                    :dict-value="filterDictValue"
                    @input="loadModels()"
                    @clear="clearFilters"
                >
                    <date-range
                        :date-start.sync="filters.date_start"
                        :date-end.sync="filters.date_end"
                        :label="$t('msg.dates')"
                        @change="loadModels()"
                        outlined
                        dense
                    ></date-range>
                    <v-autocomplete
                        v-model="filters.account"
                        :label="$t('msg.account')"
                        :items="getAccounts"
                        item-value="id"
                        item-text="name"
                        :loading="accountsLoading"
                        chips
                        small-chips
                        clearable
                        hide-details
                        autocomplete="off"
                        outlined
                        dense
                    ></v-autocomplete>
                    <v-autocomplete
                        v-model="filters.group"
                        :label="$t('msg.group')"
                        :items="groups"
                        item-value="id"
                        item-text="name"
                        :loading="groupsLoading"
                        chips
                        small-chips
                        clearable
                        hide-details
                        autocomplete="off"
                        outlined
                        dense
                    ></v-autocomplete>
                    <v-autocomplete
                        v-model="filters.tags"
                        :label="$t('msg.markers')"
                        :items="tags"
                        chips
                        deletable-chips
                        small-chips
                        clearable
                        multiple
                        hide-details
                        autocomplete="off"
                        outlined
                        dense
                    ></v-autocomplete>
                    <v-select
                        v-model="filters.type"
                        :label="$t('msg.type')"
                        clearable
                        hide-details
                        :items="types"
                        outlined
                        dense
                    >
                        <template v-slot:selection="{item}">
                            {{ $t('typeTransaction.'+item) }}
                        </template>
                        <template v-slot:item="{item}">
                            {{ $t('typeTransaction.'+item) }}
                        </template>
                    </v-select>
                </filters>
            </template>
            <v-progress-linear
                v-if="loading"
                indeterminate
            ></v-progress-linear>
            <v-data-table
                v-if="!$vuetify.breakpoint.smAndDown && !loading"
                :headers="headers"
                :items="filteredModels.items"
                :loading="loading"
                hide-default-footer
                :items-per-page="3000"
                class="v-data-table_small"
                :mobile-breakpoint="0"
            >
                <template v-slot:item.date="{item}">
                    <span style="white-space: nowrap">{{ item.date }}</span>
                </template>
                <template v-slot:item.group_id="{item}">
                    {{ findItem(groups, item.group_id) }}
                </template>
                <template v-slot:item.name="{item}">
                    {{ item.name }}
                  <tags
                    v-model="item.tag"
                  ></tags>
                </template>
                <template v-slot:item.account_id="{item}">
                <span
                    v-if="item.is_transfer && item.parent_id !== null"
                    class="sub-account"
                >{{ findItem(accounts, item.sub_account_id) }} <v-icon>mdi-arrow-right</v-icon></span>
                    {{ findItem(accounts, item.account_id) }}
                    <span
                        v-if="item.is_transfer && item.parent_id === null"
                        class="sub-account"
                    ><v-icon>mdi-arrow-right</v-icon> {{ findItem(accounts, item.sub_account_id) }}</span>
                </template>
                <template v-slot:item.type="{item}">
                    <v-chip
                        small
                        :class="{
                            'bg-green-light': !item.is_transfer && item.type === 'income',
                            'bg-red-light': !item.is_transfer && item.type === 'expenses',
                            'bg-grey-light': item.is_transfer,
                        }"
                    >{{ item.is_transfer ? $t('typeTransaction.transfer') : $t('typeTransaction.'+item.type) }}</v-chip>
                </template>
                <template v-slot:item.val="{item}">
                    {{ item.type === 'income' ? '+' : '-' }} {{ item.val | numFormat('0,0.00') }} {{ findItem(currencies, item.currency_id, 'symbol') }}
                </template>
                <template v-slot:item.balance="{item}">
                    {{ (isAccountBalance ? item.balance_account : item.balance) | numFormat('0,0.00') }} {{ findItem(currencies, item.currency_id, 'symbol') }}
                </template>
                <template v-slot:item.actions="{item}">
                    <actions
                        del="deleteTransaction"
                        :id="item.id"
                        @deleted="deletedItem()"
                    >
                        <template v-slot:before>
                            <v-btn
                                fab
                                small
                                color="primary"
                                class="cell-action__btn"
                                elevation="0"
                                :to="{name: item.is_transfer ? 'edit_transfer' : 'edit_transaction', params: {id: item.parent_id !== null ? item.parent_id : item.id}}"
                            >
                                <v-icon dark class="fix-icon-btn">mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                    </actions>
                </template>
                <template v-slot:body.append>
                    <tr class="v-data-table__sums" v-if="filteredModels.items.length > 0">
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>
                            <div class="sum">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-icon class="sum__tooltip" color="grey lighten-1" v-on="on">mdi-information</v-icon>
                                    </template>
                                    <span>{{ $t('txt.finance.infoSum') }}</span>
                                </v-tooltip>
                                <div>
                                    <div
                                        v-for="(currency, key) in filteredModels.sum"
                                        :key="key"
                                        class="sum__row"
                                    >
                                        +{{ currency.income | numFormat('0,0.00') }} / {{ currency.expenses | numFormat('0,0.00') }} {{ currency.symbol }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td align="end">
                            <div
                                v-for="(currency, key) in filteredModels.total"
                                :key="key"
                                class="sum__row"
                            >
                                {{ currency.val | numFormat('0,0.00') }} {{ currency.symbol }}
                            </div>
                        </td>
                        <td></td>
                    </tr>
                </template>
            </v-data-table>
            <div v-if="$vuetify.breakpoint.smAndDown && !loading" class="mobile-table">
                <div
                    v-for="(item, key) in filteredModels.items"
                    :key="key"
                    class="mobile-table__item"
                    @click="$router.push({name: item.is_transfer ? 'edit_transfer' : 'edit_transaction', params: {id: item.parent_id !== null ? item.parent_id : item.id}})"
                >
                    <div class="mobile-table__left">
                        <div class="mobile-table__row">{{ item.is_transfer ? $t('msg.transfer') : findItem(groups, item.group_id) }}</div>
                        <div class="mobile-table__row">
                        <span
                            v-if="item.is_transfer && item.parent_id !== null"
                            class="sub-account"
                        >{{ findItem(accounts, item.sub_account_id) }} <v-icon>mdi-arrow-right</v-icon></span>
                            {{ findItem(accounts, item.account_id) }}
                            <span
                                v-if="item.is_transfer && item.parent_id === null"
                                class="sub-account"
                            ><v-icon>mdi-arrow-right</v-icon> {{ findItem(accounts, item.sub_account_id) }}</span>
                        </div>
                        <div class="mobile-table__row mobile-table__row_small">
                            {{ item.name }}
                        </div>
                        <div class="mobile-table__row">
                          <tags
                            v-model="item.tag"
                          ></tags>
                        </div>
                    </div>
                    <div class="mobile-table__right">
                        <div class="mobile-table__row mobile-table__row_small">{{ item.date }}</div>
                        <div class="mobile-table__row mobile-table__row_big">
                            {{ item.type === 'income' ? '+' : '-' }} {{ item.val | numFormat('0,0.00') }} {{ findItem(currencies, item.currency_id, 'symbol') }}
                        </div>
                        <div class="mobile-table__row mobile-table__row_small">
                            {{ (isAccountBalance ? item.balance_account : item.balance) | numFormat('0,0.00') }} {{ findItem(currencies, item.currency_id, 'symbol') }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="$vuetify.breakpoint.smAndDown && !loading" class="total mt-3">
                <table>
                    <tbody>
                    <tr>
                        <td>{{ $t('msg.income') }}:</td>
                        <td>
                            <div
                                v-for="(currency, key) in filteredModels.sum"
                                :key="key"
                                class="sum__row"
                            >
                                + {{ currency.income | numFormat('0,0.00') }} {{ currency.symbol }}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('msg.expenses') }}:</td>
                        <td>
                            <div
                                v-for="(currency, key) in filteredModels.sum"
                                :key="key"
                                class="sum__row"
                            >
                                {{ currency.expenses | numFormat('0,0.00') }} {{ currency.symbol }}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ !!this.filters.account ? this.$t('msg.accountBalance') : this.$t('msg.overallBalance') }}:</td>
                        <td>
                            <div
                                v-for="(currency, key) in filteredModels.total"
                                :key="key"
                            >
                                {{ currency.val | numFormat('0,0.00') }} {{ currency.symbol }}
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </card>
    </div>
</template>

<script>
    import Actions from '../../../components/ui/Actions';
    import DateRange from '../../../components/ui/DateRange';
    import Tag from "../../../components/ui/Tag";
    import Filters from "../../../components/ui/Filters";
    import { format, parse, lastDayOfMonth, startOfMonth } from 'date-fns';
    import BtnsNav from "../../../components/ui/BtnsNav";
    import Tags from "@/components/ui/Tags";

    export default {
        components: {Actions, DateRange, Tag, Filters, BtnsNav, Tags},
        data() {
            return {
                loading: false,
                models: [],
                tags: [],
                accounts: [],
                accountsLoading: false,
                groups: [],
                groupsLoading: false,
                currencies: [],
                types: [],
                filters: {
                    date_start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
                    date_end: format(lastDayOfMonth(new Date()), 'yyyy-MM-dd'),
                    tags: null,
                    account: null,
                    group: null,
                    type: null
                },
                delayLoadModels: (function(){
                    let timer = 0;
                    return function(callback, ms){
                        clearTimeout(timer);
                        timer = setTimeout(callback, ms);
                    };
                })(),
                btns: [
                    {label: this.$t('msg.monthBudget'), to: {name: 'month_budget'}},
                ]
            }
        },
        computed: {
            headers() {
                return [
                    {text: this.$t('msg.date'), value: 'date', align: 'start', sortable: false},
                    {text: this.$t('msg.group'), value: 'group_id', align: 'start', sortable: false},
                    {text: this.$t('msg.name'), value: 'name', align: 'start', sortable: false},
                    {text: this.$t('msg.account'), value: 'account_id', align: 'start', sortable: false},
                    {text: this.$t('msg.incomeExpenses'), value: 'val', align: 'end', sortable: false},
                    {text: !!this.filters.account ? this.$t('msg.accountBalance') : this.$t('msg.overallBalance'), value: 'balance', align: 'end', sortable: false},
                    {text: this.$t('msg.actions'), value: 'actions', align: 'center', sortable: false, width: 100}
                ];
            },
            isAccountBalance() {
                return !!this.filters.account;
            },
            filteredModels() {
                let items = this.models.filter(model => {

                    let filterTags = () => {
                        if (this.filters.tags === null || this.filters.tags.length === 0) return true;
                        for (let key in model.tag) {
                            if (this.filters.tags.includes(model.tag[key])) {
                                return true;
                            }
                        }
                        return false;
                    };

                    let filterAccount = () => {
                        if (this.filters.account === undefined || this.filters.account === null) return true;
                        return model.account_id === this.filters.account;
                    };

                    let filterGroup = () => {
                        if (this.filters.group === undefined || this.filters.group === null) return true;
                        return model.group_id === this.filters.group;
                    };

                    let filterType = () => {
                        if (this.filters.type === undefined || this.filters.type === null) return true;
                        return model.type === this.filters.type;
                    };

                    return filterTags() && filterAccount() && filterGroup() && filterType();
                });

                let sum = {};
                let total = {};

                items.forEach(item => {
                    if (!item.is_transfer) {
                        if (sum[item.currency_id] === undefined) sum[item.currency_id] = {
                            income: 0,
                            expenses: 0,
                            symbol: this.findItem(this.currencies, item.currency_id, 'symbol')
                        };

                        if (item.type === 'income') {
                            sum[item.currency_id].income += item.val;
                        } else {
                            sum[item.currency_id].expenses -= item.val;
                        }
                    }

                    let fieldName = (this.filters.account === undefined || this.filters.account === null) ? 'balance' : 'balance_account';

                    if (total[item.currency_id] === undefined) total[item.currency_id] = {val: item[fieldName], symbol: this.findItem(this.currencies, item.currency_id, 'symbol')};
                    else total[item.currency_id].val = item[fieldName];
                });

                return {
                    items,
                    sum,
                    total
                };
            },
            getAccounts() {
                let items = [];

                this.accounts.forEach(item => {

                    let currency = '';

                    const cur = this.currencies.find(v => v.id === item.currency_id);
                    if (cur !== undefined) currency = cur.symbol;

                    items.push({
                        id: item.id,
                        name: item.name + ' ' + currency
                    });
                });

                return items;
            },
            filterDictValue() {
                let type = {};
                this.types.forEach(item => {
                    type[item] = this.$t('typeTransaction.'+item);
                });

                let account = {};
                this.accounts.forEach(item => {
                    account[item.id] = item.name;
                });

                let group = {};
                this.groups.forEach(item => {
                    group[item.id] = item.name;
                });

                return {
                    type,
                    account,
                    group,
                    date_start: (v) => {
                      return this.dateString(v);
                    },
                    date_end: (v) => {
                      return this.dateString(v);
                    }
                }
            },
            getQueryCreateTransaction() {
                let query = {};

                if (this.filters.date_start && this.filters.date_start === this.filters.date_end) {
                    query.date = this.filters.date_start;
                }

                if (this.filters.account) {
                    query.account_id = this.filters.account;
                }

                return query;
            }
        },
        methods: {
            clearFilters() {

                this.filters.date_start = format(startOfMonth(new Date()), 'yyyy-MM-dd');
                this.filters.date_end = format(lastDayOfMonth(new Date()), 'yyyy-MM-dd');
                this.filters.account = null;
                this.filters.group = null;
                this.filters.tags = [];
                this.filters.type = null;

                this._loadModels();
            },
            loadModels() {
                this.delayLoadModels(() => {
                    this._loadModels();
                }, 500);
            },
            _loadModels() {
                this.loading = true;
                let params = {
                    date_start: this.filters.date_start,
                    date_end: this.filters.date_end
                };
                this.$store.dispatch('loadTransactions', {params})
                    .then(res => {
                        res.forEach((item, i) => {
                            let source = item.date.slice(0, 10);
                            let d = parse(source, 'yyyy-MM-dd', new Date());
                            res[i].date = format(d, 'dd') + ' ' + this.$t('monthShort.'+format(d, 'MMMM'));
                        });
                        this.models = res;
                        this.collectTags();
                    })
                    .finally(() => this.loading = false);
            },
            collectTags() {
                this.tags = [];
                this.models.forEach(model => {
                    if (model.tag === null) return;
                    model.tag.forEach(tag => {
                        if (!this.tags.includes(tag)) {
                            this.tags.push(tag);
                        }
                    });
                })
            },
            loadAccounts() {
                this.accountsLoading = true;
                this.$store.dispatch('loadAccounts')
                    .then(res => this.accounts = res)
                    .finally(() => this.accountsLoading = false);
            },
            loadGroups() {
                this.groupsLoading = true;
                this.$store.dispatch('loadGroups', {params: {}})
                    .then(res => this.groups = res)
                    .finally(() => this.groupsLoading = false);
            },
            loadCurrencies() {
                this.$store.dispatch('loadCurrencies')
                    .then(res => this.currencies = res);
            },
            loadTypes() {
                this.$store.dispatch('loadGroupTypes')
                    .then(res => this.types = res);
            },
            deletedItem() {
                this.loadModels();
            }
        },
        created() {
            this.loadModels();
            this.loadAccounts();
            this.loadGroups();
            this.loadCurrencies();
            this.loadTypes();
        }
    }
</script>

<style lang="scss">
    .sum {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        .sum__row {
            text-align: right;
        }
        .sum__tooltip {
            margin-right: 10px;
        }
    }
    .sub-account {
        opacity: 0.5;

        .v-icon {
            font-size: 16px;
        }
    }
</style>